import { Button } from "@9amhealth/shared";
import styled from "@emotion/styled";
import { type FC, useEffect, useRef } from "react";
import { useBloc } from "src/state/state";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import NotificationSettingsBloc from "src/ui/components/NotificationSettings/NotificationSettingsBloc";
import AppDialog from "src/ui/components/AppDialog/AppDialog";
import Translate from "src/ui/components/Translate/Translate";
import { AppQueryPopupsController } from "../AppQueryPopups/AppQueryPopupsBloc";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";
import GradientBackground from "../HealthSync/GradientBackground";
import { Blac } from "blac-next";
import { onAppActive } from "src/hybrid/appLifecycle";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";

const Container = styled.div`
  width: min(100%, ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px);
  margin: auto;
  padding: 2rem 1rem;
  display: grid;
  height: 100%;
  justify-items: center;
  text-align: center;
  grid-template:
    "empty"
    "center"
    "bottom";
  grid-template-rows: 1fr auto 1fr;
`;

const CenterContent = styled.div`
  grid-area: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const ClipboardImage = styled.div`
  background-image: url("https://cdn.sanity.io/images/g38rxyoc/production/76a7c3281e83c88cb8a42ad38bf19da3ed58ae4d-981x822.png?w=440");
  width: min(70%, 220px);
  aspect-ratio: 1.2;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;

const Title = styled.h2`
  color: var(--Greys-Charcoal, #212121);
  text-wrap: pretty;
  max-width: 340px;
  text-align: center;
  font-family: var(--font-family);
  font-size: 1.2rem;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: -0.4px;
`;

const NotNowButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text-secondary);
  font-size: 1rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  font-family: var(--font-family);
`;

const WaitingForFileBeingReviewed: FC = () => {
  const [
    { pushNotifications, showConfirmModal },
    {
      togglePushNotifications,
      openNativeSettings,
      hideModal,
      reloadUserPreferences
    }
  ] = useBloc(NotificationSettingsBloc, {
    create: () => new NotificationSettingsBloc()
  });

  const hasInteracted = useRef(false);
  const hasClosedPopup = useRef(false);

  const closePopupAndRefreshTasks = () => {
    if (hasClosedPopup.current) {
      return;
    }

    hasClosedPopup.current = true;
    AppQueryPopupsController.closePopup();
    void Blac.getBloc(TaskManagementBloc).loadProgramTasks(
      KnownProgram.ONBOARDING
    );
  };

  useEffect(() => {
    void reloadUserPreferences();
  }, [reloadUserPreferences]);

  useEffect(() => {
    return onAppActive(() => {
      void reloadUserPreferences();
    });
  }, [reloadUserPreferences]);

  useEffect(() => {
    if (hasInteracted.current && pushNotifications) {
      closePopupAndRefreshTasks();
    }
  }, [pushNotifications]);

  const handleTurnOnNotifications = () => {
    hasInteracted.current = true;
    togglePushNotifications(true);
  };

  return (
    <GradientBackground>
      <Container role="status" aria-label="">
        {showConfirmModal === "pushNotifications" && (
          <AppDialog
            confirmText="enable.feature"
            confirmArrow={false}
            onCancel={hideModal}
            onConfirm={openNativeSettings}
          >
            <h5>
              <Translate msg="notification.turnOn" />
            </h5>
            <p>
              <Translate msg="notification.turnOn.description" />
            </p>
          </AppDialog>
        )}

        <CenterContent>
          <ClipboardImage aria-hidden="true" />
          <Title>
            <Translate msg="task.yourInitialLab.yourReportIsBeingReviewed.notification" />
          </Title>
          <Button theme="sunrise" onPress={handleTurnOnNotifications}>
            <Translate msg="initialLabs.requisitionReady.turnOnNotifications" />
          </Button>
          <NotNowButton type="button" onClick={closePopupAndRefreshTasks}>
            <Translate msg="notNow" />
          </NotNowButton>
        </CenterContent>
      </Container>
    </GradientBackground>
  );
};

export default WaitingForFileBeingReviewed;
